import { useEffect, useMemo, useState } from 'react';
import { LogOutIcon } from 'lucide-react';
import { generatePath, useSearchParams } from 'react-router-dom';
import {
  CurrentUserDto,
  UserLoginStatus,
  useAcceptTermsOfService,
  useGetOrganization,
  useGetUser,
} from '@sib/api';
import { useLogout, useUserSetupStore } from '@sib/auth';
import { NO_DATA_PLACEHOLDER } from '@sib/config';
import { useTranslation } from '@sib/i18n';
import { useDialog } from '@sib/ui/hooks';
import {
  Calendar,
  Home,
  Account,
  Billing,
  Chart,
  Documents,
  Info,
  TeamOrg,
  Savings,
  Feedback,
  LocationList,
  Vendors,
  YourTickets,
  QuestionMark,
  BillPay,
  ContractTerms,
} from '@sib/ui/icons';
import { useOrganizationFromSearch } from '@sib/utils';
import { updateUserId } from '@spendbrain/common/utils';
import {
  ABOUTSIB_URL,
  DIALOG_QUERY_PARAM,
  PHONE_MODAL_DISMISSED_KEY_PREFIX,
  TOS_DIALOG_QUERY_VALUE,
} from '@spendbrain/config/constants';
import { paths } from '@spendbrain/config/paths';
import { useSpendbrainRoutesAccess } from '@spendbrain/hooks';
import { useFileManagerFileUploadStore } from '@spendbrain/stores/fileUploadStore';
import { useFeedbackStore } from '../../stores';

export const useOrganizationSetup = () => {
  const { setOrganization } = useUserSetupStore();
  const organization = useOrganizationFromSearch();

  useEffect(() => {
    if (!organization) return;
    setOrganization({
      id: organization.id,
      name: organization.name,
    });
  }, [organization, setOrganization]);
};

export const useBeforeUnloadWarning = () => {
  const isUploading = useFileManagerFileUploadStore().getAreFilesUploading();

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isUploading) event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isUploading]);
};

interface UsePhoneNumberModalProps {
  user: CurrentUserDto | undefined;
  refetchUser: () => void;
}

export const usePhoneNumberModal = ({
  user,
  refetchUser,
}: UsePhoneNumberModalProps) => {
  const PHONE_MODAL_DISMISSED_KEY = `${PHONE_MODAL_DISMISSED_KEY_PREFIX}-${user?.id}`;

  const [isDismissed, setIsDismissed] = useState(
    () => sessionStorage.getItem(PHONE_MODAL_DISMISSED_KEY) === 'true'
  );

  const isOpen =
    user?.userLoginStatus === UserLoginStatus.Uncompleted && !isDismissed;

  const handleSuccess = (statusUpdated: boolean) => {
    if (!user?.id) return;

    if (statusUpdated) {
      sessionStorage.removeItem(PHONE_MODAL_DISMISSED_KEY);
      setIsDismissed(false);
      refetchUser();
    } else {
      sessionStorage.setItem(PHONE_MODAL_DISMISSED_KEY, 'true');
      setIsDismissed(true);
    }
  };

  return {
    isOpen,
    handleSuccess,
  };
};

interface UseTermsOfServiceModalProps {
  organizationId?: string;
}

export const useTermsOfServiceModal = ({
  organizationId,
}: UseTermsOfServiceModalProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasGlobalAccess } = useUserSetupStore();
  const { data: organization } = useGetOrganization(organizationId);
  const { mutate: handleAcceptTermsOfService } = useAcceptTermsOfService();
  const { isSuccess: isUserLoaded } = useGetUser();

  const canAccept = hasGlobalAccess === false;

  const acceptedByUser = useMemo(() => {
    if (organization?.termsOfServiceAccepted !== true) return null;

    return {
      name:
        organization.tosAcceptedBy?.name ||
        organization.tosAcceptedBy?.email ||
        NO_DATA_PLACEHOLDER,
      acceptedAt: organization.tosAcceptedAt ?? organization.updatedAt,
    };
  }, [organization]);

  const hasDialogParam =
    searchParams.get(DIALOG_QUERY_PARAM) === TOS_DIALOG_QUERY_VALUE;

  const isOpen =
    isUserLoaded &&
    Boolean(organizationId) &&
    organization !== undefined &&
    hasDialogParam;

  const closeDialog = () => {
    setSearchParams(
      prev => {
        if (prev.get(DIALOG_QUERY_PARAM) !== TOS_DIALOG_QUERY_VALUE)
          return prev;

        const next = new URLSearchParams(prev);
        next.delete(DIALOG_QUERY_PARAM);

        return next;
      },
      { replace: true }
    );
  };

  const handleAccept = () => {
    if (!organizationId || !canAccept) return;
    handleAcceptTermsOfService({ organizationId });
    closeDialog();
  };

  return {
    isOpen,
    handleAccept,
    onClose: closeDialog,
    acceptedByUser,
    canAccept,
    isDismissible: !acceptedByUser,
  };
};

export const useSidebarItems = () => {
  const { t } = useTranslation();
  const logoutDialog = useDialog();
  const { openFeedbackDialog } = useFeedbackStore();
  const {
    organization: { id: organizationId },
    hasGlobalAccess,
  } = useUserSetupStore();

  const ticketsPath = hasGlobalAccess
    ? generatePath(paths.tickets.employee.root, { orgId: organizationId })
    : paths.tickets.client.root;

  const { isRouteAvailable } = useSpendbrainRoutesAccess();
  const { mutate: logout } = useLogout({ onLogout: updateUserId });

  const items = useMemo(
    () =>
      [
        {
          href: paths.subsidiaryOrganization,
          title: t('sidebar.subsidiaryOrganization'),
          icon: TeamOrg,
        },
        {
          href: paths.dashboard,
          title: t('sidebar.dashboard'),
          icon: Home,
        },
        {
          href: paths.savings,
          title: t('sidebar.savingsAndSpendAnalysis'),
          icon: Savings,
        },
        {
          href: paths.services.root,
          title: t('sidebar.services'),
          icon: Chart,
        },
        {
          href: paths.documents.root,
          title: t('sidebar.documents'),
          icon: Documents,
        },
        {
          href: paths.locations,
          title: t('sidebar.locations'),
          icon: LocationList,
        },
        { href: paths.reports.root, title: t('sidebar.reports'), icon: Chart },
        {
          href: ticketsPath,
          title: t('sidebar.tickets'),
          icon: YourTickets,
        },
        {
          href: paths.upcomingEvents,
          title: t('sidebar.upcomingEvents'),
          icon: Calendar,
        },
        {
          href: paths.teamMembers.root,
          title: t('sidebar.teamMembers'),
          icon: TeamOrg,
        },
        {
          href: paths.account,
          title: t('sidebar.account'),
          icon: Account,
        },
        {
          href: paths.billing.root,
          title: t('sidebar.billings'),
          icon: Billing,
        },

        {
          href: paths.vendors.root,
          title: t('sidebar.vendors'),
          icon: Vendors,
        },
        {
          href: paths.billPay,
          title: t('sidebar.billPay'),
          icon: BillPay,
        },
        {
          href: paths.contractTerms,
          title: t('sidebar.contractTerms'),
          icon: ContractTerms,
        },
        {
          href: paths.help,
          title: t('sidebar.help'),
          icon: QuestionMark,
        },
        {
          href: ABOUTSIB_URL,
          title: t('sidebar.about'),
          icon: Info,
        },
        {
          title: t('sidebar.sendFeedback'),
          icon: Feedback,
          onClick: openFeedbackDialog,
        },
        {
          title: t('logout.title'),
          icon: LogOutIcon,
          onClick: logoutDialog.handleOpen,
        },
      ].filter(({ href }) => !href || isRouteAvailable(href)),
    [
      isRouteAvailable,
      openFeedbackDialog,
      t,
      ticketsPath,
      logoutDialog.handleOpen,
    ]
  );

  return { items, logoutDialog, logout };
};
